import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CircleNotch, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TIMEZONES } from "@/components/admin/constants";
import { readAvatar } from "@/lib/avatar";
import { formatPhone } from "@/lib/phone";

const NONE = "__none__";

function Fld({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{label}</Label>
      {children}
    </div>
  );
}

async function loadProfileRefsInto(setRefs) {
  try {
    const [locations, users, divisions] = await Promise.all([
      api.get("/locations"), api.get("/users/options"), api.get("/reference/service-divisions"),
    ]);
    setRefs({ locations: locations.data, users: users.data, divisions: divisions.data });
  } catch (e) {
    toast.error("Profile options could not be loaded.");
  }
}

export function CreateUserDialog({ open, onClose, onDone }) {
  const { user: me } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", role: "salesperson", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("User created — complete their profile from Edit");
      setForm({ name: "", email: "", role: "salesperson", password: "" });
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Create User</DialogTitle>
          <DialogDescription>Provision a new CRM account. The user signs in with the password you set.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="Full name">
            <Input required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="new-user-name" />
          </Fld>
          <Fld label="Email">
            <Input type="email" required value={form.email} onChange={set("email")} className="rounded-sm" data-testid="new-user-email" />
          </Fld>
          <Fld label="Role">
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="salesperson">Salesperson</SelectItem>
                {me?.role === "global_administrator" && <SelectItem value="administrator">Administrator</SelectItem>}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Temporary password">
            <Input type="text" required minLength={8} value={form.password} onChange={set("password")} className="rounded-sm" placeholder="Min 8 characters" data-testid="new-user-password" />
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-new-user">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditUserDialog({ user, onClose, onDone }) {
  const { user: me } = useAuth();
  const [form, setForm] = useState({});
  const [refs, setRefs] = useState({ locations: [], users: [], divisions: [] });
  const [loading, setLoading] = useState(false);
  const helpers = useRef({ loadProfileRefsInto });
  const loadRefs = useCallback(() => helpers.current.loadProfileRefsInto(setRefs), [helpers, setRefs]);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name, role: user.role, user_status: user.user_status,
      job_title: user.job_title || "", mobile_phone: user.mobile_phone || "",
      office_phone: user.office_phone || "", supervisor_id: user.supervisor_id || NONE,
      base_location_id: user.base_location_id || NONE, division_ids: user.division_ids || [],
      timezone: user.timezone || NONE, annual_quota: user.annual_quota ?? "",
      commission_program: user.commission_program || "", profile_picture: user.profile_picture || null,
    });
    loadRefs();
  }, [user, setForm, loadRefs]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const toggleDivision = (id) => setForm((f) => ({
    ...f,
    division_ids: f.division_ids.includes(id) ? f.division_ids.filter((x) => x !== id) : [...f.division_ids, id],
  }));

  const pickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await readAvatar(file);
      setForm((f) => ({ ...f, profile_picture: dataUrl }));
    } catch {
      toast.error("The image could not be read.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Explicit nulls CLEAR nullable profile fields on the server.
      const body = {
        name: form.name, role: form.role, user_status: form.user_status,
        job_title: form.job_title || null,
        mobile_phone: form.mobile_phone || null,
        office_phone: form.office_phone || null,
        supervisor_id: form.supervisor_id === NONE ? null : form.supervisor_id,
        base_location_id: form.base_location_id === NONE ? null : form.base_location_id,
        division_ids: form.division_ids,
        timezone: form.timezone === NONE ? null : form.timezone,
        annual_quota: form.annual_quota === "" ? null : Number(form.annual_quota),
        commission_program: form.commission_program || null,
        profile_picture: form.profile_picture || null,
      };
      await api.patch(`/users/${user.id}`, body);
      toast.success("User updated");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;
  const supervisors = refs.users.filter((u) => u.id !== user.id);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Edit User</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-4">
            {form.profile_picture ? (
              <img src={form.profile_picture} alt="" className="h-14 w-14 rounded-sm object-cover" data-testid="edit-user-avatar-preview" />
            ) : (
              <span className="flex h-14 w-14 items-center justify-center rounded-sm bg-[#032852] text-lg font-bold text-white">
                {(form.name || "").split(" ").map((n) => n[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
              </span>
            )}
            <label className="flex cursor-pointer items-center gap-1.5 rounded-sm border border-[#E4E4E7] px-3 py-1.5 text-xs font-semibold text-[#032852] hover:bg-[#F4F4F5]">
              <UploadSimple size={14} /> Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} data-testid="edit-user-avatar-input" />
            </label>
            {form.profile_picture && (
              <button type="button" className="text-xs font-semibold text-[#B91C1C]"
                onClick={() => setForm((f) => ({ ...f, profile_picture: null }))} data-testid="edit-user-avatar-remove">
                Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Full name">
              <Input required value={form.name || ""} onChange={set("name")} className="rounded-sm" data-testid="edit-user-name" />
            </Fld>
            <Fld label="Job title">
              <Input value={form.job_title || ""} onChange={set("job_title")} className="rounded-sm" placeholder="e.g. Sales Executive" data-testid="edit-user-job-title" />
            </Fld>
            <Fld label="Role">
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="salesperson">Salesperson</SelectItem>
                  {me?.role === "global_administrator" && <SelectItem value="administrator">Administrator</SelectItem>}
                  {form.role === "global_administrator" && <SelectItem value="global_administrator">Global Administrator</SelectItem>}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Status">
              <Select value={form.user_status} onValueChange={set("user_status")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-user-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Mobile phone">
              <Input value={form.mobile_phone || ""} onChange={set("mobile_phone")} onBlur={(e) => set("mobile_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="edit-user-mobile" />
            </Fld>
            <Fld label="Office phone">
              <Input value={form.office_phone || ""} onChange={set("office_phone")} onBlur={(e) => set("office_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="edit-user-office-phone" />
            </Fld>
            <Fld label="Supervisor">
              <Select value={form.supervisor_id} onValueChange={set("supervisor_id")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-user-supervisor"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {supervisors.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Base location">
              <Select value={form.base_location_id} onValueChange={set("base_location_id")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-user-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {refs.locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Time zone">
              <Select value={form.timezone} onValueChange={set("timezone")}>
                <SelectTrigger className="rounded-sm" data-testid="edit-user-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Use device time zone</SelectItem>
                  {TIMEZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="Annual quota $">
              <Input type="number" min="0" value={form.annual_quota ?? ""} onChange={set("annual_quota")} className="rounded-sm" data-testid="edit-user-quota" />
            </Fld>
          </div>
          <Fld label="Commission program">
            <Input value={form.commission_program || ""} onChange={set("commission_program")} className="rounded-sm" placeholder="e.g. Standard 2026 program" data-testid="edit-user-commission-program" />
          </Fld>
          <Fld label="Assigned divisions">
            <div className="grid grid-cols-2 gap-1.5" data-testid="edit-user-divisions">
              {refs.divisions.map((d) => (
                <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm text-[#09090B]">
                  <Checkbox checked={(form.division_ids || []).includes(d.id)}
                    onCheckedChange={() => toggleDivision(d.id)} data-testid={`edit-user-division-${d.id}`} />
                  {d.name}
                </label>
              ))}
            </div>
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-edit-user">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setPw("");
  }, [user, setPw]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Reset Password</DialogTitle>
          <DialogDescription>Set a new password for {user?.name} ({user?.email}).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Fld label="New password">
            <Input type="text" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-sm" placeholder="Min 8 characters" data-testid="reset-new-password" />
          </Fld>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-reset-password">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Global-Admin bulk purge of TEST accounts. Uses /users/purge-preview so every
// account is listed with its linked-record counts; protected accounts (system,
// GA, self, or any linked business records) can never be selected. Requires
// typing PURGE as the explicit confirmation.
export function PurgeDialog({ open, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setConfirmText("");
    setRows(null);
    api.get("/users/purge-preview")
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Purge preview could not be loaded."));
  }, [open]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const confirmed = confirmText.trim().toUpperCase() === "PURGE";

  const submit = async () => {
    if (selected.length === 0) return toast.error("Select at least one account to purge");
    if (!confirmed) return toast.error('Type PURGE to confirm permanent removal');
    setLoading(true);
    try {
      const { data } = await api.post("/users/purge", { user_ids: selected });
      const skippedMsg = data.skipped.length ? ` · ${data.skipped.length} skipped (protected)` : "";
      toast.success(`${data.purged.length} account(s) permanently purged${skippedMsg}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const linkedSummary = (r) =>
    Object.entries(r.linked_records)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${n} ${k}`)
      .join(", ");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="purge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Purge Test Accounts</DialogTitle>
          <DialogDescription>
            Permanent removal for test accounts only. System-provisioned accounts, administrators
            and any account holding business records are protected and cannot be selected.
          </DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-[#032852]" /></div>
        ) : (
          <>
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-sm border border-[#E4E4E7] p-2">
              {rows.map((r) => (
                <label key={r.id}
                  className={`flex items-start gap-2.5 rounded-sm px-2 py-1.5 text-sm ${r.eligible ? "cursor-pointer hover:bg-[#F4F4F5]" : "cursor-not-allowed opacity-60"}`}
                  data-testid={`purge-option-${r.email}`}>
                  <Checkbox checked={selected.includes(r.id)} disabled={!r.eligible}
                    onCheckedChange={() => r.eligible && toggle(r.id)} className="mt-0.5" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-[#09090B]">{r.name}</span>
                    <span className="block truncate text-xs text-[#6D6D70]">{r.email}</span>
                    {r.eligible ? (
                      <span className="block text-[11px] font-semibold text-[#457d18]" data-testid={`purge-linked-${r.email}`}>
                        0 linked records — eligible for permanent removal
                      </span>
                    ) : (
                      <span className="block text-[11px] font-semibold text-[#B45309]" data-testid={`purge-linked-${r.email}`}>
                        Protected — {r.protected_reason}{r.linked_total > 0 ? ` (${linkedSummary(r)})` : ""}
                      </span>
                    )}
                  </span>
                </label>
              ))}
              {rows.length === 0 && <p className="py-6 text-center text-sm text-[#6D6D70]">No accounts found.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
                Type <span className="text-[#B91C1C]">PURGE</span> to confirm permanent removal of {selected.length} account(s)
              </Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PURGE" className="rounded-sm" data-testid="purge-confirm-input" />
            </div>
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading || selected.length === 0 || !confirmed}
            className="rounded-sm bg-[#EF4444] font-semibold text-white hover:bg-[#DC2626]" data-testid="submit-purge">
            {loading ? <CircleNotch size={18} className="animate-spin" /> : `Permanently purge ${selected.length || ""} account(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
